// src/pages/WarehousesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import CategoryIcon from "@mui/icons-material/Category";
import PostAddIcon from "@mui/icons-material/PostAdd";
import LayersIcon from "@mui/icons-material/Layers";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function WarehousesMenu() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth(); // ✅ Hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isRTL = i18n.language === 'ar';
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? Number(storedCompanyId) : null;

  const [stockTypes, setStockTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('warehousesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then(res => {
        const types = (res.data || []).filter(t =>
          ["إدخال", "إخراج", "بضاعة أول مدة", "مناقلة مخزنية", "تسوية جردية"].includes(t.name)
        );
        setStockTypes(types);
      })
      .catch(err => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('warehousesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // دالة لمطابقة الأيقونات لأنواع الفواتير الديناميكية
  const getStockTypeIcon = (name) => {
    const iconSize = { xs: 40, sm: 48, md: 56, lg: 64 };
    if (name === "إدخال") {
      return <MoveToInboxIcon sx={{ fontSize: iconSize, color: "#0288d1" }} />;
    } else if (name === "إخراج") {
      return <ExitToAppIcon sx={{ fontSize: iconSize, color: "#d32f2f" }} />;
    } else if (name === "بضاعة أول مدة") {
      return <HomeWorkIcon sx={{ fontSize: iconSize, color: "#7b1fa2" }} />;
    } else if (name === "مناقلة مخزنية") {
      return <SyncAltIcon sx={{ fontSize: iconSize, color: "#ff9800" }} />;
    } else if (name === "تسوية جردية") {
      return <AssignmentIcon sx={{ fontSize: iconSize, color: "#607d8b" }} />;
    }
    return null;
  };

  // دالة لترجمة أسماء أنواع المخزون
  const getTranslatedStockTypeName = (name) => {
    if (name === "إدخال") {
      return t('warehousesMenu.stockIn');
    } else if (name === "إخراج") {
      return t('warehousesMenu.stockOut');
    } else if (name === "بضاعة أول مدة") {
      return t('warehousesMenu.openingStock');
    } else if (name === "مناقلة مخزنية") {
      return "مناقلة مخزنية";
    } else if (name === "تسوية جردية") {
      return "تسوية جردية";
    }
    return name;
  };

  const definitionsSection = [
    hasPermission(PERMISSIONS.PRODUCT_VIEW) && {
      name: t('warehousesMenu.productsList'),
      icon: <InventoryIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#4caf50" }} />,
      link: "/productList"
    },
    hasPermission(PERMISSIONS.PRODUCT_CREATE) && {
      name: t('warehousesMenu.addProduct'),
      icon: <CategoryIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#8bc34a" }} />,
      link: "/add-product"
    },
    hasPermission(PERMISSIONS.STOCK_MOVE) && {
      name: t('warehousesMenu.warehousesList'),
      icon: <WarehouseIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#795548" }} />,
      link: "/add-stock"
    },
    hasPermission(PERMISSIONS.CATEGORY_MANAGE) && {
      name: t('warehousesMenu.productCategories'),
      icon: <SettingsIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#607d8b" }} />,
      link: "/add-category"
    },
    hasPermission(PERMISSIONS.UNIT_MANAGE) && {
      name: t('warehousesMenu.addUnit'),
      icon: <PostAddIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#7b1fa2" }} />,
      link: "/add-unit"
    },
    hasPermission(PERMISSIONS.STOCK_MOVE) && {
      name: t('warehousesMenu.importSystem'),
      icon: <LayersIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#009688" }} />,
      link: "/add-importpage",
    },
  ].filter(Boolean);

  const operationsSection = [
    ...stockTypes.filter(t => {
      const name = t.name;
      if (name === "إدخال") return hasPermission(PERMISSIONS.INVOICE_STOCK_IN_CREATE);
      if (name === "إخراج") return hasPermission(PERMISSIONS.INVOICE_STOCK_OUT_CREATE);
      if (name === "بضاعة أول مدة") return hasPermission(PERMISSIONS.INVOICE_OPENING_STOCK_CREATE);
      if (name === "مناقلة مخزنية") return hasPermission(PERMISSIONS.STOCK_MOVE);
      if (name === "تسوية جردية") return hasPermission(PERMISSIONS.STOCK_MOVE);
      return false;
    }).map(type => ({
      name: getTranslatedStockTypeName(type.name),
      icon: getStockTypeIcon(type.name),
      link: `/invoices/new/${type.invoiceTypeId}`,
    })),
  ];

  const reportsSection = [
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.AllSalesInvoices'),
      icon: <ViewListIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#0288d1" }} />,
      link: "/reports/all-invoices?&invoiceTypeId=7",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.detailedProductMovement'),
      icon: <ViewListIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#0288d1" }} />,
      link: "/reports/all-products-movement",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.aggregatedProductMovement'),
      icon: <ViewListIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#0288d1" }} />,
      link: "/reports/AggregatedReport",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.inventoryReport'),
      icon: <ViewListIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#0288d1" }} />,
      link: "/reports/InventoryReport",
    },
  ].filter(Boolean);

  // دالة مساعدة لعرض الأيقونات داخل Grid
  const renderGridItems = (items) => (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {items.map((item, idx) => (
        <Grid
          item
          xs={6}        // جوال: عمودين
          sm={4}        // تابلت: 3
          md={3}        // لابتوب: 4
          lg={2.4}      // شاشة كبيرة: 5
          xl={2}
          key={item.name + idx}
        >
          <ButtonBase
            onClick={() => item.link && navigate(item.link)}
            disabled={!item.link}
            sx={{
              width: "100%",
              minHeight: { xs: 140, sm: 160, md: 180, lg: 200 },
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              border: "2px solid #e0e0e0",
              backgroundColor: "#ffffff",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                borderColor: "#1976d2",
                backgroundColor: "#f8fafc",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed"
              }
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={{ xs: 2, sm: 2.5, md: 3 }}
              textAlign="center"
            >
              {item.icon}
              <Typography
                variant="body1"
                fontWeight="600"
                mt={{ xs: 1, sm: 1.5 }}
                color="text.primary"
                sx={{
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                    md: '1.1rem',
                    lg: '1.15rem'
                  },
                  lineHeight: 1.3,
                  px: 1
                }}
              >
                {item.name}
              </Typography>
              {item.count != null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.75rem' }}
                >
                  {item.count}
                </Typography>
              )}
            </Box>
          </ButtonBase>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return (
      <Container 
        maxWidth={false}
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={4}>
          <CircularProgress size={isMobile ? 40 : 50} />
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            sx={{ mt: 2 }}
          >
            {t('warehousesMenu.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container 
        maxWidth={false}
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={2}>
          <Typography 
            color="error"
            variant={isMobile ? "body1" : "h6"}
          >
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header مع أزرار التنقل */}
      <Paper 
       elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            fontWeight="bold" 
            color="white"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.5rem'
              },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t('warehousesMenu.dashboardTitle')}
          </Typography>

          
        </Box>
      </Paper>

      {/* قسم التعاريف */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #fff3e0',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #ff9800',
            paddingBottom: 1.5,
            color: '#f57c00',
          }}
        >
          🏗️ {t('warehousesMenu.definitionsSection')}
        </Typography>
        {renderGridItems(definitionsSection)}
      </Paper>

      {/* Operations Section */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #fff3e0',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="warning.dark"
          sx={{
            fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' },
            borderBottom: '3px solid #f57c00',
            paddingBottom: 1.5,
          }}
        >
          ⚙️ {t('warehousesMenu.operationsSection')}
        </Typography>
        {renderGridItems(operationsSection)}
      </Paper>

      {/* Reports Section */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #e0f2f1',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          sx={{
            fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' },
            borderBottom: '3px solid #009688',
            paddingBottom: 1.5,
            color: '#009688',
          }}
        >
          📊 {t('warehousesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportsSection)}
      </Paper>
    </Container>
  );
}
